import { usePermissions } from "@podtrac/common/accounts";
import React, { useMemo } from "react";
import { FaEllipsisV } from "react-icons/fa";
import { NavLink, useNavigate } from "react-router-dom";
import DropMenu from "../shared/InaccessibleMenu";
import PaidFeatureButton from "../shared/PaidFeatureButton";
import { useAccount, useAccountID } from "../store/account";
import { createPortal } from "react-dom";

const itemBuilder = (account, permissions) =>
    [
        {
            label: "Key Metrics",
            path: "",
            end: true,
            enabled: true
        },
        {
            label: "Rankings",
            path: "rankings",
            enabled:
                permissions?.canViewAccountRankings &&
                (account?.isPaidPlan || account?.offerUpgrade),
            paid: "The Rankings report is a premium feature available in the Grow Your Show Plan."
        },
        {
            label: "Advertising",
            path: "advertising",
            enabled: account?.hasCampaigns && permissions?.canViewPublisherCampaigns
        },
        {
            label: "Episode Detail",
            path: "episode-detail",
            enabled:
                account &&
                account.episodeDetailEnabled &&
                permissions &&
                !permissions.limitedPermissionspermissions?.canViewPublisherCampaigns
        },
        {
            label: "Account Settings",
            path: "settings",
            enabled: permissions?.canViewAccountSettings
        }
    ].filter(i => i.enabled);

export function AccountMenu() {
    const navigate = useNavigate();
    const accountID = useAccountID();
    const { data: account } = useAccount();
    const { data: permissions } = usePermissions(accountID);

    const items = useMemo(() => itemBuilder(account, permissions), [account, permissions]);

    return (
        <DropMenu>
            {({
                isOpen,
                close,
                createWrapperProps,
                createButtonProps,
                createMenuProps,
                createItemProps
            }) => (
                <span
                    {...createWrapperProps({
                        className: "drop-menu"
                    })}
                >
                    <button
                        {...createButtonProps({
                            className: isOpen ? "active" : undefined
                        })}
                    >
                        <FaEllipsisV />
                        <span className="tip" role="tooltip">
                            More
                        </span>
                    </button>
                    {isOpen ? createPortal(
                        <ul {...createMenuProps({ className: "drop-menu-menu" })}>
                            {items.map(({ label, path, paid }) =>
                                paid ? (
                                    <PaidFeatureButton
                                        key={path}
                                        hasFeature={account.isPaidPlan}
                                        dialogContent={paid}
                                    >
                                        {({ hasFeature, openModal }) => (
                                            <li
                                                {...createItemProps({
                                                    onClick: hasFeature
                                                        ? () => {
                                                              navigate(path);
                                                          }
                                                        : openModal
                                                })}
                                            >
                                                {label}
                                            </li>
                                        )}
                                    </PaidFeatureButton>
                                ) : (
                                    <li
                                        key={path}
                                        {...createItemProps({
                                            onClick() {
                                                navigate(path);
                                            }
                                        })}
                                    >
                                        {label}
                                    </li>
                                )
                            )}
                        </ul>,
                        document.body
                    ) : null}
                </span>
            )}
        </DropMenu>
    );
}

export function AccountTopNav() {
    const accountID = useAccountID();
    const { data: account = {} } = useAccount();
    const { data: permissions } = usePermissions(accountID);
    const items = useMemo(() => itemBuilder(account, permissions), [account, permissions]);
    return (
        <nav>
            <h1>{account.accountName || "\u2001"}</h1>
            <ul>
                {items.map(({ path, label, className, end, paid }) => (
                    <li key={path}>
                        {paid ? (
                            <PaidFeatureButton hasFeature={account.isPaidPlan} dialogContent={paid}>
                                {({ hasFeature, openModal }) => (
                                    <NavLink
                                        onClick={hasFeature ? () => {} : openModal}
                                        to={path}
                                        end={end}
                                        className={className}
                                    >
                                        {label}
                                    </NavLink>
                                )}
                            </PaidFeatureButton>
                        ) : (
                            <NavLink to={path} end={end} className={className}>
                                {label}
                            </NavLink>
                        )}
                    </li>
                ))}
            </ul>
        </nav>
    );
}
